import React, { useCallback, useRef, useEffect, useState, memo } from 'react';
import Element from './Element';
import './Canvas.css';
import ImageUploader from './ImageElement';
import LayerPanel from './LayerPanel';
import { generateThumbnail } from '../utils/thumbnailGenerator';

// Ensure no circular dependencies
const MemoizedElement = memo(Element);

function Canvas({ elements: propElements, selectedElement, setSelectedElement, updateElement, deleteElement, isPreviewMode, addElement, getDefaultStyle, deviceView, eventId, invitationId, initialElements, onThumbnailGenerated }) {
  // Force deviceView to be 'desktop' until mobile view is fully implemented
  const actualDeviceView = 'desktop'; // Override deviceView to always be desktop for now
  const canvasRef = useRef(null);
  const [canvasBounds, setCanvasBounds] = useState(null);
  const [isDraggingNewElement, setIsDraggingNewElement] = useState(false);
  const [newElementType, setNewElementType] = useState(null);
  const [elements, setElements] = useState(propElements);
  
  // Calculate and update canvas boundaries when component mounts or window resizes
  useEffect(() => {
    const updateCanvasBounds = () => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        
        // Set bounds based on canvas container dimensions
        setCanvasBounds({
          left: rect.left,
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height
        });
        
        console.log("Canvas bounds updated:", {
          width: rect.width,
          height: rect.height,
          deviceView: actualDeviceView, // Use actualDeviceView instead of deviceView
          isPreviewMode
        });
      }
    };
    
    // Call immediately to set initial bounds
    updateCanvasBounds();
    
    // Then also call when window resizes
    window.addEventListener('resize', updateCanvasBounds);
    
    // Create a MutationObserver to watch for changes to the canvas
    const observer = new MutationObserver(updateCanvasBounds);
    
    // Start observing the canvas for attribute changes that might affect size
    if (canvasRef.current) {
      observer.observe(canvasRef.current, { 
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: true,
        subtree: true
      });
    }
    
    // Also observe the document body for layout changes
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    
    // Set a timeout to update bounds again after all DOM changes settle
    const timeoutId = setTimeout(updateCanvasBounds, 250);
    
    return () => {
      window.removeEventListener('resize', updateCanvasBounds);
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [actualDeviceView, isPreviewMode]); // Add actualDeviceView and isPreviewMode as dependencies
  
  // Adjust canvas height based on elements and mode
  useEffect(() => {
    if (!canvasRef.current) return;
    
    if (isPreviewMode) {
      // In preview mode, apply appropriate styles based on device view
      if (actualDeviceView === 'desktop') {
        // Desktop preview - use desktop width
        canvasRef.current.style.width = '1140px';
        canvasRef.current.style.maxWidth = '1140px';
      } else if (actualDeviceView === 'mobile') {
        // Mobile preview - use mobile width
        canvasRef.current.style.width = '375px';
        canvasRef.current.style.maxWidth = '375px';
      }
      
      // Common styles for preview mode, but don't override background styles
      canvasRef.current.style.margin = '0 auto';
      canvasRef.current.style.paddingBottom = '0';
      canvasRef.current.style.paddingTop = '0';
      canvasRef.current.style.paddingLeft = '0';
      canvasRef.current.style.paddingRight = '0';
      canvasRef.current.style.position = 'relative';
      canvasRef.current.style.transform = 'none';
      
      // Only set default background styles if no background element is present
      const hasBackgroundElement = elements.some(el => el.type === 'background');
      if (!hasBackgroundElement) {
        canvasRef.current.style.backgroundColor = 'white';
        canvasRef.current.style.backgroundImage = '';
      }
      
      // Always add shadow for visual distinction
      canvasRef.current.style.boxShadow = '0 0 20px rgba(0, 0, 0, 0.1)';
      
      // Add a small delay to ensure elements are rendered before measuring
      setTimeout(() => {
        // Calculate the bottom-most element to set the proper height
        let maxBottom = 0;
        elements.forEach(element => {
          const el = document.getElementById(`element-${element.id}`);
          if (el) {
            const bottom = element.position.y + el.offsetHeight;
            maxBottom = Math.max(maxBottom, bottom);
          }
        });
        
        // Set exact height based on content plus some padding
        canvasRef.current.style.height = `${maxBottom + 100}px`;
        canvasRef.current.style.minHeight = `${maxBottom + 100}px`;
      }, 100);
    } else {
      // In edit mode, ensure we have plenty of space at the bottom for adding new elements
      let maxBottom = 0;
      elements.forEach(element => {
        const el = document.getElementById(`element-${element.id}`);
        if (el) {
          const bottom = element.position.y + el.offsetHeight;
          maxBottom = Math.max(maxBottom, bottom);
        }
      });
      
      // Set minimum height to be greater of viewport height or content + padding
      const minHeight = Math.max(window.innerHeight, maxBottom + 300);
      canvasRef.current.style.minHeight = `${minHeight}px`;
      canvasRef.current.style.paddingBottom = '200px';
    }
  }, [elements, isPreviewMode, actualDeviceView]);
  
  // Add a useEffect to handle canvas resizing based on device view
  useEffect(() => {
    if (!canvasRef.current || isPreviewMode) return; // Skip this in preview mode
    
    const container = canvasRef.current.parentElement;
    
    // Set width based on device type
    switch (actualDeviceView) {
      case 'desktop':
        container.style.width = '100%';
        container.style.maxWidth = '1140px';
        break;
      case 'mobile':
        container.style.width = '375px';
        container.style.maxWidth = '375px';
        break;
      default:
        container.style.width = '100%';
        container.style.maxWidth = '1140px';
    }
    
    // Rearrange elements if they don't fit
    if (actualDeviceView !== 'desktop') {
      const containerWidth = container.offsetWidth;
      
      // Adjust elements that go beyond the new container width
      elements.forEach(element => {
        const el = document.getElementById(`element-${element.id}`);
        if (!el) return;
        
        const elementRight = element.position.x + el.offsetWidth;
        
        // If element overflows the container width
        if (elementRight > containerWidth) {
          // Calculate new x position to keep it within bounds
          const newX = Math.max(0, containerWidth - el.offsetWidth);
          
          // Update element position
          updateElement(element.id, {
            position: { ...element.position, x: newX }
          });
        }
      });
    }
  }, [actualDeviceView, elements, updateElement, isPreviewMode]);
  
  // Update elements when prop changes
  useEffect(() => {
    if (propElements && Array.isArray(propElements)) {
      setElements(propElements);
    }
  }, [propElements]);
  
  // In your useEffect that initializes elements from initialElements
  useEffect(() => {
    console.log("Canvas received initialElements:", initialElements?.length || 0);
    if (initialElements && Array.isArray(initialElements)) {
      // Use this to reset elements completely based on initialElements
      setElements(JSON.parse(JSON.stringify(initialElements)));
    }
  }, [initialElements]); // Only dependency is initialElements
  
  // Generate thumbnail when in preview mode
  useEffect(() => {
    // Only trigger thumbnail generation when entering preview mode
    if (isPreviewMode && canvasRef.current && typeof onThumbnailGenerated === 'function') {
      console.log('🔍 [Canvas] Preview mode activated, attempting to generate thumbnail');
      console.log('🔍 [Canvas] Canvas element:', canvasRef.current);
      console.log('🔍 [Canvas] Canvas dimensions:', {
        width: canvasRef.current.offsetWidth,
        height: canvasRef.current.offsetHeight,
        elementsCount: elements.length,
        elementTypes: elements.map(e => e.type).join(', '),
        hasImages: elements.some(e => e.type === 'image'),
        hasText: elements.some(e => e.type === 'text'),
        hasBackground: elements.some(e => e.type === 'background')
      });
      
      // Log detailed info about each element for debugging
      elements.forEach((element, index) => {
        console.log(`🔍 [Canvas] Element ${index + 1}/${elements.length}:`, {
          id: element.id,
          type: element.type,
          position: element.position,
          style: element.style ? 
            {width: element.style.width, height: element.style.height} : 'No style',
          content: element.content ? 
            (typeof element.content === 'string' ? 
              (element.content.length > 30 ? element.content.substring(0, 30) + '...' : element.content) 
              : 'Complex content') 
            : 'No content'
        });
      });
      
      // Add a longer delay to ensure canvas has fully rendered with all images loaded
      setTimeout(async () => {
        console.log('🔍 [Canvas] Generating thumbnail after initial delay...');
        let thumbnail = null;
        let attempts = 0;
        const maxAttempts = 3;
        
        while (!thumbnail && attempts < maxAttempts) {
          attempts++;
          console.log(`🔄 [Canvas] Thumbnail generation attempt ${attempts}/${maxAttempts}...`);
          
          try {
            thumbnail = await generateCanvasThumbnail();
            
            if (!thumbnail) {
              console.log(`⚠️ [Canvas] Attempt ${attempts}/${maxAttempts} failed, ${attempts < maxAttempts ? 'retrying' : 'giving up'}...`);
              if (attempts < maxAttempts) {
                const delay = 1000 * attempts; // Increasing delay with each attempt
                console.log(`⏱️ [Canvas] Waiting ${delay}ms before next attempt...`);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            } else {
              console.log(`✅ [Canvas] Attempt ${attempts}/${maxAttempts} succeeded.`);
            }
          } catch (error) {
            console.error(`❌ [Canvas] Error in thumbnail generation attempt ${attempts}/${maxAttempts}:`, error);
            if (attempts < maxAttempts) {
              const delay = 1000 * attempts;
              console.log(`⏱️ [Canvas] Waiting ${delay}ms before next attempt after error...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
        
        if (thumbnail) {
          console.log('✅ [Canvas] Successfully generated thumbnail, length:', thumbnail.length);
          console.log('✅ [Canvas] Thumbnail data starts with:', thumbnail.substring(0, 50) + '...');
          onThumbnailGenerated(thumbnail);
        } else {
          console.error('❌ [Canvas] Failed to generate thumbnail after', maxAttempts, 'attempts');
          // Let the parent component know we couldn't generate a thumbnail
          onThumbnailGenerated(null);
        }
      }, 1500); // Increased from 1000ms to 1500ms
    }
  }, [isPreviewMode, elements, onThumbnailGenerated]); // Add onThumbnailGenerated to dependencies
  
  // Method to generate canvas thumbnail
  const generateCanvasThumbnail = async () => {
    if (!canvasRef.current) {
      console.error('❌ [Canvas] Cannot generate thumbnail: Canvas ref is null');
      return null;
    }
    
    try {
      console.log('🔍 [Canvas] Starting canvas thumbnail generation...');
      console.time('thumbnailGeneration');
      
      // Check for valid canvas state
      const canvasElement = canvasRef.current;
      console.log('🔍 [Canvas] Canvas element state:', {
        width: canvasElement.offsetWidth,
        height: canvasElement.offsetHeight,
        childNodes: canvasElement.childNodes.length,
        childElements: Array.from(canvasElement.children).length,
        className: canvasElement.className,
        isVisible: !!canvasElement.offsetParent,
        isConnected: canvasElement.isConnected,
        hasComputedStyle: !!window.getComputedStyle,
        background: window.getComputedStyle(canvasElement).background
      });
      
      // Check if canvas has visual dimensions
      if (canvasElement.offsetWidth <= 0 || canvasElement.offsetHeight <= 0) {
        console.error('❌ [Canvas] Canvas has zero dimensions, cannot capture thumbnail');
        return null;
      }
      
      // Try to verify that all images are loaded
      const imageElements = canvasElement.querySelectorAll('img');
      console.log(`🔍 [Canvas] Canvas contains ${imageElements.length} image elements`);
      
      // Log info about the images for debugging
      imageElements.forEach((img, index) => {
        console.log(`🔍 [Canvas] Image ${index + 1}/${imageElements.length}:`, {
          src: img.src ? (img.src.length > 50 ? img.src.substring(0, 50) + '...' : img.src) : 'No src',
          complete: img.complete,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
          width: img.width,
          height: img.height,
          visible: !!img.offsetParent,
          display: window.getComputedStyle(img).display,
          error: img.error
        });
      });
      
      // Try using the direct capture function instead of html2canvas directly
      try {
        console.log('🔍 [Canvas] Trying direct capture first (better for complex canvases)...');
        
        // First try to use captureFullPageScreenshot if available
        const { captureFullPageScreenshot } = await import('../utils/thumbnailGenerator');
        
        if (typeof captureFullPageScreenshot === 'function') {
          // Try to capture the entire canvas area
          const fullScreenshot = await captureFullPageScreenshot();
          if (fullScreenshot && fullScreenshot.startsWith('data:image/')) {
            console.log('✅ [Canvas] Direct page screenshot succeeded, length:', fullScreenshot.length);
            console.timeEnd('thumbnailGeneration');
            return fullScreenshot;
          } else {
            console.log('⚠️ [Canvas] Direct page screenshot failed, falling back to html2canvas');
          }
        }
      } catch (directCaptureError) {
        console.log('⚠️ [Canvas] Error in direct capture, falling back to html2canvas:', directCaptureError.message);
      }
      
      // Now call the thumbnail generator
      console.log('🔍 [Canvas] Calling thumbnail generator with canvas element...');
      const thumbnail = await generateThumbnail(canvasElement);
      console.timeEnd('thumbnailGeneration');
      
      if (thumbnail) {
        console.log('✅ [Canvas] Thumbnail generated successfully, length:', thumbnail.length);
        console.log('✅ [Canvas] Thumbnail type:', typeof thumbnail);
        console.log('✅ [Canvas] Thumbnail starts with correct format:', thumbnail.startsWith('data:image/'));
      } else {
        console.error('❌ [Canvas] Thumbnail generation returned null or empty result');
      }
      
      return thumbnail;
    } catch (error) {
      console.error('❌ [Canvas] Error generating canvas thumbnail:', error);
      console.error('❌ [Canvas] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      // Add browser and environment info for debugging
      console.log('🔍 [Canvas] Browser environment:', {
        userAgent: navigator.userAgent,
        windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
        devicePixelRatio: window.devicePixelRatio,
        windowLocation: window.location.href
      });
      
      return null;
    }
  };
  
  // Method to export canvas as thumbnail (for external use)
  const exportCanvasThumbnail = useCallback(async () => {
    return await generateCanvasThumbnail();
  }, []);
  
  // Expose the thumbnail generation method to parent components
  useEffect(() => {
    if (typeof onThumbnailGenerated === 'function') {
      // Expose the method through a ref or callback if needed
      onThumbnailGenerated.getCanvasThumbnail = exportCanvasThumbnail;
    }
  }, [onThumbnailGenerated, exportCanvasThumbnail]);
  
  const handleCanvasClick = (e) => {
    if (!isPreviewMode) {
      setSelectedElement(null);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDraggingNewElement(true);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingNewElement(false);
    
    const elementType = e.dataTransfer.getData('elementType');
    if (!elementType) return;
    
    // Calculate drop position relative to canvas
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // No boundary constraints - allow elements to be positioned anywhere
    addElement(elementType, { x, y });
  };
  
  // Add a CSS class to the canvas for overflow handling
  useEffect(() => {
    if (!canvasRef.current) return;
    // Set overflow to hidden to ensure elements outside the canvas are clipped
    canvasRef.current.style.overflow = 'hidden';
  }, [canvasRef]);
  
  // Replace enforceElementBoundaries with a function that doesn't restrict positioning
  const enforceElementBoundaries = (id, newPosition, elementWidth, elementHeight) => {
    // No boundary constraints - allow free positioning
    return newPosition;
  };
  
  // Allow elements to be dragged anywhere
  const handleElementDrag = (id, position, elementWidth, elementHeight) => {
    if (isPreviewMode) return;
    // Update element position without restrictions
    updateElement(id, { position });
  };
  
  // Modify the enforceSafeZone function to be much less restrictive
  useEffect(() => {
    if (!canvasBounds || isPreviewMode || !elements || elements.length === 0) return;
    
    // Only check for elements that are completely outside the viewable area
    // and move them back into view if they are too far away
    const enforceSafeZone = () => {
      let elementsModified = false;
      
      // Create a deep copy to avoid mutating while iterating
      const safeElements = [...elements];
      
      // Check each element and only adjust those completely outside the viewable area
      safeElements.forEach((element, index) => {
        if (!element.position) return;
        
        // Get element DOM reference to get actual dimensions
        const elementDom = document.getElementById(`element-${element.id}`);
        if (!elementDom) return;
        
        const elementWidth = elementDom.offsetWidth;
        const elementHeight = elementDom.offsetHeight;
        
        // Only adjust elements that are COMPLETELY outside the viewable area
        // with a very large margin (1000px) to allow positioning far outside
        const isFarOutside = 
          element.position.x > canvasBounds.width + 1000 || 
          element.position.y > canvasBounds.height + 1000 ||
          element.position.x + elementWidth < -1000 ||
          element.position.y + elementHeight < -1000;
        
        // If element is far outside, bring it back into a reasonable area
        if (isFarOutside) {
          // Position it at the edge of the canvas
          const safeX = Math.min(Math.max(element.position.x, -elementWidth + 20), canvasBounds.width - 20);
          const safeY = Math.min(Math.max(element.position.y, -elementHeight + 20), canvasBounds.height - 20);
          
          console.log(`Element ${element.id} extremely far outside bounds, adjusting position:`, {
            from: element.position,
            to: { x: safeX, y: safeY }
          });
          
          // Update the element position
          safeElements[index] = {
            ...element,
            position: {
              x: safeX,
              y: safeY
            }
          };
          
          elementsModified = true;
        }
      });
      
      // Update elements if any were modified
      if (elementsModified) {
        console.log("Updating elements to enforce safe zone");
        setElements(safeElements);
        
        // Update parent component's state as well
        safeElements.forEach(element => {
          const originalElement = elements.find(e => e.id === element.id);
          if (originalElement && 
              (originalElement.position.x !== element.position.x || 
               originalElement.position.y !== element.position.y)) {
            updateElement(element.id, {
              position: element.position
            });
          }
        });
      }
    };
    
    // Run immediately with a small delay to ensure DOM elements are rendered
    const timeoutId = setTimeout(enforceSafeZone, 300);
    
    return () => clearTimeout(timeoutId);
  }, [elements, canvasBounds, isPreviewMode, updateElement]);
  
  // Add this function to handle element updates properly
  const handleElementUpdate = (id, updatedElement) => {
    console.log("Canvas handling element update:", id, updatedElement);
    
    // Create a deep copy of the elements array
    const updatedElements = elements.map(el => 
      el.id === id ? { ...el, ...updatedElement } : el
    );
    
    // Log the before/after for debugging
    console.log("Canvas elements before update:", elements);
    console.log("Canvas elements after update:", updatedElements);
    
    // Update local state
    setElements(updatedElements);
    
    // Also pass to parent component
    updateElement(id, updatedElement);
  };
  
  // Check for elements
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && elements.length > 0) {
      // Only log when the elements array length changes
      if (!window.__LAST_ELEMENTS_COUNT__ || window.__LAST_ELEMENTS_COUNT__ !== elements.length) {
        console.log(`Canvas elements updated: ${elements.length} items`);
        window.__LAST_ELEMENTS_COUNT__ = elements.length;
      }
    }
  }, [elements.length]);
  
  const handleAddImage = (imageElement) => {
    // Add the uploaded image element to your elements array
    setElements(prevElements => [...prevElements, imageElement]);
  };
  
  // Add a useEffect to handle responsive layout when device view changes
  useEffect(() => {
    // Skip in preview mode - only adjust when user changes device view in editor
    if (isPreviewMode) return;
    
    // Get container width for the current device view
    const containerWidth = canvasRef.current?.offsetWidth || 0;
    if (!containerWidth) return;
    
    // For desktop view, restore original positions if available
    if (actualDeviceView === 'desktop') {
      // Nothing specific needed for desktop view - elements stay in their original positions
      return;
    }
    
    // For mobile view, adjust elements to fit container
    setElements(prevElements => {
      if (!Array.isArray(prevElements)) return prevElements;
      
      // Create a deep copy to avoid mutation
      const adjustedElements = JSON.parse(JSON.stringify(prevElements));
      
      // First pass: adjust width and position of all elements
      adjustedElements.forEach(element => {
        const elementWidth = parseInt(element.style.width) || 0;
        
        // Store original positions if not already stored
        if (!element.originalPosition && element.position) {
          element.originalPosition = { ...element.position };
        }
        
        if (actualDeviceView === 'mobile') {
          // For mobile view, maintain relative positioning but scale widths
          // Cap width to container width minus margins
          if (elementWidth > containerWidth - 40) {
            element.style.width = `${containerWidth - 40}px`;
          }
          
          // Do NOT override the x/y positions - maintain original ordering
          // Just ensure elements are visible in the mobile container
          if (element.position.x > containerWidth) {
            element.position.x = Math.max(20, containerWidth / 2);
          }
        }
      });
      
      // We're no longer stacking elements in mobile view
      // This preserves the original layout ordering
      return adjustedElements;
    });
  }, [actualDeviceView, canvasBounds, isPreviewMode]);

  // When switching back to larger screens, restore original positions
  useEffect(() => {
    if (actualDeviceView === 'desktop' && !isPreviewMode) {
      setElements(prevElements => {
        if (!Array.isArray(prevElements)) return prevElements;
        
        return prevElements.map(element => {
          // If element has stored original position, restore it
          if (element.originalPosition) {
            return {
              ...element,
              position: { ...element.originalPosition }
            };
          }
          return element;
        });
      });
    }
  }, [actualDeviceView, isPreviewMode]);
  
  // Add a function to update element z-index
  const updateElementZIndex = (id, newZIndex) => {
    // Update the element z-index
    updateElement(id, { zIndex: newZIndex });
  };

  // Add a function to move element forward (increment z-index)
  const moveElementForward = (id) => {
    const element = elements.find(el => el.id === id);
    if (!element) return;
    
    // Find the element with the next highest z-index
    const sortedElements = [...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const currentIndex = sortedElements.findIndex(el => el.id === id);
    
    // If already at the top, do nothing
    if (currentIndex === sortedElements.length - 1) return;
    
    const nextElement = sortedElements[currentIndex + 1];
    const newZIndex = (nextElement.zIndex || 0) + 1;
    
    // Update the element
    updateElement(id, { zIndex: newZIndex });
  };

  // Add a function to move element backward (decrement z-index)
  const moveElementBackward = (id) => {
    const element = elements.find(el => el.id === id);
    if (!element) return;
    
    // Find the element with the next lowest z-index
    const sortedElements = [...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const currentIndex = sortedElements.findIndex(el => el.id === id);
    
    // If already at the bottom, do nothing
    if (currentIndex === 0) return;
    
    const prevElement = sortedElements[currentIndex - 1];
    const newZIndex = (prevElement.zIndex || 0) - 1;
    
    // Update the element
    updateElement(id, { zIndex: newZIndex });
  };

  // Add a function to bring element to front (highest z-index)
  const bringElementToFront = (id) => {
    // Find the highest z-index
    const highestZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
    
    // Update the element
    updateElement(id, { zIndex: highestZIndex + 1 });
  };

  // Add a function to send element to back (lowest z-index)
  const sendElementToBack = (id) => {
    // Find the lowest z-index
    const lowestZIndex = Math.min(...elements.map(el => el.zIndex || 0), 0);
    
    // Update the element
    updateElement(id, { zIndex: lowestZIndex - 1 });
  };

  // Sort elements by z-index before rendering
  const sortedElements = [...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

  return (
    <div 
      ref={canvasRef}
      className={`canvas ${isPreviewMode ? 'preview-mode' : ''} device-${actualDeviceView}`} 
      onClick={handleCanvasClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDraggingNewElement(false)}
    >
      {Array.isArray(sortedElements) && sortedElements.map(element => (
        <MemoizedElement
          key={element.id}
          id={`element-${element.id}`}
          element={element}
          isSelected={element.id === selectedElement}
          onSelect={setSelectedElement}
          onUpdate={handleElementUpdate}
          onDelete={deleteElement}
          isPreviewMode={isPreviewMode}
          canvasBounds={canvasBounds}
          eventId={eventId}
          invitationId={invitationId}
          data-preserve-position={element.preservePosition ? "true" : "false"}
        />
      ))}
      
      {isDraggingNewElement && (
        <div className="drop-indicator">Drop to add element</div>
      )}
    </div>
  );
}

export default Canvas; 